/**
 * Utility functions for handling AI ingestion suggestions.
 */

// Ingestion results and program state share one vocabulary — this list only says which
// fields the drawer may suggest into a submission.
export const SUGGESTION_FIELDS = [
  "insured",
  "riskDescription",
  "riskSummary",
  "insuredAddress",
  "reference",
  "notes",
  "inception",
  "expiration",
  "naics",
  "industryClass",
  "categoryCode",
  "brokerage",
  "profit",
  "currency",
  "paymentTerms",
  "maximumPossibleLoss",
];

/**
 * Get all unique suggestions for a given field across all ingested files.
 *
 * @param {string} fieldKey - The field key (e.g., 'insured', 'riskDescription')
 * @param {Object} ingestionData - The ingestion data object
 * @returns {Array} Array of suggestion objects with value, sources, and confidence
 */
export const getSuggestionsForField = (fieldKey, ingestionData) => {
  if (!ingestionData?.results) {
    return [];
  }

  const suggestions = [];
  const seenValues = new Set();

  // Iterate through all files
  Object.entries(ingestionData.results).forEach(([fileId, fileData]) => {
    const fieldData = fileData[fieldKey];

    if (fieldData && fieldData.value != null) {
      const value = fieldData.value;

      if (!seenValues.has(value)) {
        seenValues.add(value);
        suggestions.push({
          value,
          sources: fieldData.source_locations || [],
          confidence: fieldData.confidence || 0,
          reasoning: fieldData.reasoning,
          fileName: fileData.file_name,
          fileId: fileData.file_id,
          runId: fileData.run_id,
        });
      }
    }
  });

  // Sort by confidence (highest first)
  return suggestions.sort((a, b) => b.confidence - a.confidence);
};

/**
 * Get all available suggestions across all fields.
 * Useful for checking if any suggestions are available.
 *
 * @param {Object} ingestionData - The ingestion data object
 * @returns {Object} Object mapping field keys to their suggestions
 */
export const getAllSuggestions = (ingestionData) => {
  const allSuggestions = {};

  SUGGESTION_FIELDS.forEach((fieldKey) => {
    const suggestions = getSuggestionsForField(fieldKey, ingestionData);
    if (suggestions.length > 0) {
      allSuggestions[fieldKey] = suggestions;
    }
  });

  return allSuggestions;
};

/**
 * Check if there are any suggestions available.
 *
 * @param {Object} ingestionData - The ingestion data object
 * @returns {boolean} True if suggestions are available
 */
export const hasSuggestions = (ingestionData) => {
  if (!ingestionData?.results) {
    return false;
  }

  return Object.keys(ingestionData.results).length > 0;
};

/**
 * Format source information for display.
 *
 * @param {Array} sources - Array of source location objects
 * @returns {string} Formatted source string
 */
export const formatSources = (sources) => {
  if (!sources || sources.length === 0) {
    return "";
  }

  return sources
    .map((source) => `${source.filename} (p.${source.page_number})`)
    .join(", ");
};

// Validators per field: (value, context) => transformedValue | null
const FIELD_VALIDATORS = {
  insured: (v) => v,
  riskDescription: (v) => v,
  riskSummary: (v) => v,
  insuredAddress: (v) => v,
  reference: (v) => v,
  notes: (v) => v,
  inception: (v) => v,
  expiration: (v) => v,
  currency: (v, ctx) => (ctx.currencyCodesList?.includes(v) ? v : null),
  brokerage: (v) => (typeof v === "number" && !isNaN(v) ? v : null),
  profit: (v) => (typeof v === "number" && !isNaN(v) ? v : null),
  naics: (v) => (v?.code != null && v?.title ? v : null),
  categoryCode: (v) => (v?.code != null && v?.title ? v : null),
  industryClass: (v, ctx) => {
    const match = ctx.industryClassChoices?.find(
      (c) => c.name.toLowerCase() === String(v).toLowerCase()
    );
    return match ? match.key : null;
  },
};

/**
 * Extract best field values from a single file's ingestion data.
 * Each field is run through its validator/transformer; only fields
 * that pass validation are included.
 *
 * @param {Object} fileData - Single file's ingestion result data
 * @param {number} minConfidence - Minimum confidence threshold (0-1)
 * @param {Object} context - Validation context (currencyCodesList, industryClassChoices, dispatch, claimsApi)
 * @returns {Promise<Object>} Object mapping field keys to their best values
 */
export const extractBestFields = async (
  fileData,
  minConfidence = 0.4,
  context = {}
) => {
  if (!fileData) {
    return {};
  }

  const bestFields = {};

  SUGGESTION_FIELDS.forEach((fieldKey) => {
    const validator = FIELD_VALIDATORS[fieldKey];
    if (!validator) {
      return;
    }
    const fieldData = fileData[fieldKey];
    if (
      fieldData &&
      fieldData.value != null &&
      (fieldData.confidence || 0) >= minConfidence
    ) {
      const transformed = validator(fieldData.value, context);
      if (transformed != null) {
        bestFields[fieldKey] = transformed;
      }
    }
  });

  // Validate categoryCode and naics against search APIs
  if (bestFields.categoryCode && context.dispatch && context.claimsApi) {
    const searchTerm = bestFields.categoryCode.title || "";
    if (searchTerm.trim()) {
      const result = await context.dispatch(
        context.claimsApi.endpoints.GICSCategoryCodes.initiate({
          query: searchTerm,
        })
      );
      if (result.data && result.data.length > 0) {
        bestFields.categoryCode = {
          code: result.data[0]._CODE,
          title: result.data[0]._TITLE,
        };
      } else {
        delete bestFields.categoryCode;
      }
    } else {
      delete bestFields.categoryCode;
    }
  }

  if (bestFields.naics && context.dispatch && context.claimsApi) {
    const searchTerm = bestFields.naics.title || "";
    if (searchTerm.trim()) {
      const result = await context.dispatch(
        context.claimsApi.endpoints.NAICSCodes.initiate({ query: searchTerm })
      );
      if (result.data && result.data.length > 0) {
        bestFields.naics = {
          code: result.data[0]._CODE,
          title: result.data[0]._TITLE,
        };
      } else {
        delete bestFields.naics;
      }
    } else {
      delete bestFields.naics;
    }
  }

  return bestFields;
};
